import {
  formatDecimal,
  isNormalPositive,
  isValidAddress,
  useBalance,
} from '@bifrost-platform/bifront-sdk-react-biholder';
import { useWallet } from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import IconEverdex from '@/assets/icons/icon-everdex.svg';
import IconTooltipArrow from '@/assets/icons/icon-tooltip-arrow.svg';
import ButtonWithArrow from '@/components/ButtonWithArrow';
import { FlexBox } from '@/components/atom/FlexBox';
import { GlassPanel } from '@/components/atom/GlassPanel';
import Input from '@/components/atom/Input';
import { StyledText } from '@/components/atom/Text';
import { MEDIA_QUERY_BREAKPOINT } from '@/configs/mediaQuery';
import useEverdex from '@/hooks/api/everdex/useEverdex';
import useInitializer from '@/hooks/bifrontSdk/useInitializer';
import useEnv from '@/hooks/useEnv';
import { formatAmountDetail, formatRate } from '@/libs/formatNumber';
import { eventEverdexAddLiquidityClick } from '@/libs/gtag';
import { dialogActionAtom, isShowConnectWalletAtom } from '@/store/uiStore';
import { dashboardCardVariant } from '@/styles/motion';
import { parseNumberInput } from '@/utils/input';
import Button from '../atom/Button';
import LogoBtcusd from '../atom/LogoBtcusd';
import Skeleton from '../atom/Skeleton';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../atom/Tooltip';
import WarnMessage from '../atom/WarnMessage';

const TooltipKeyframe = keyframes`
    0% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-10px);
    }
    100% {
        transform: translateY(0);
    }
`;
const Shimmer = keyframes`
    0% {
        opacity: 0;
    }
    15% {
        opacity: 0;
        background-position: -50%;
    }
    30% {
        opacity: .7;
    }
    45% {
        opacity: 0;
        background-position: 150%;
    }
    60% {
        opacity: 0;
    }
    to {
        opacity: 0;
    }
`;

const ShimmerContainer = styled.div`
  animation: ${Shimmer} 3s infinite;
  background: linear-gradient(
    112deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 40%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0.1) 60%,
    transparent 80%
  );
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 20;
  pointer-events: none;
  background-size: 200% auto;
  background-position: 0;
`;
const CustomTooltip = styled(FlexBox)`
  position: relative;
  flex-direction: column;
  animation: ${TooltipKeyframe} 1.5s infinite;

  svg {
    position: absolute;
    rotate: 90deg;
    top: 32px;
    left: -7px;
  }
`;
const CustomTooltipContent = styled.div`
  ${(p) => p.theme.rounded.lg};
  ${(p) => p.theme.bg.grayBlue};
  backdrop-filter: blur(2px);
  padding: 12px 16px;
`;
const InputContainer = styled(FlexBox)`
  flex: 1;
`;
const InputWrapper = styled(FlexBox)`
  position: relative;

  & > label {
    align-self: flex-start;
  }
`;
const Description = styled(StyledText)`
  white-space: pre-line;
`;
const TokenWrapper = styled(FlexBox)`
  ${(p) => p.theme.bg.black};
  ${(p) => p.theme.rounded.lg};
  padding: ${(p) => p.theme.size.lg} ${(p) => p.theme.size.md};
  font-style: italic;
  line-height: 1;
`;
const EverdexButton = styled(ButtonWithArrow)<{ isLpRewardOpen?: boolean }>`
  height: ${({ isLpRewardOpen }) => (isLpRewardOpen ? '100px' : 'unset')};
  position: relative;
  overflow: hidden;
  justify-content: space-between;
`;
const BadgeContainer = styled(FlexBox)`
  display: flex;
  background-color: ${(p) => p.theme.colors.black};
  padding: ${(p) => p.theme.size.xxxs} ${(p) => p.theme.size.xxs};
  border-radius: ${(p) => p.theme.size.xs};
  justify-content: center;
  align-items: center;
  gap: ${(p) => p.theme.size.xxxs};
  white-space: nowrap;
`;
const StyledGlassPanel = styled(GlassPanel)`
  @media (${MEDIA_QUERY_BREAKPOINT}) {
    min-width: 100%;
  }
`;
const Apy = styled.span`
  display: flex;
  align-items: flex-end;
  font-size: ${(p) => p.theme.size.xl};
  ${(p) => p.theme.font.bold}

  & > strong {
    font-size: ${(p) => p.theme.size.xxxl};
    ${(p) => p.theme.font.black}
    color: ${(p) => p.theme.colors.green};
  }

  & > * {
    margin-bottom: -3px;
  }
`;
const MaxButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`;
const StyledWarnMessage = styled(WarnMessage)`
  width: fit-content;
`;

export default function DepositEverdexPanel() {
  // translate
  const { t } = useTranslation();

  // initializer
  const { syncedEverdex } = useInitializer();

  // env
  const {
    isTestnet,
    everdexLpRewardPoolPairSymbol,
    tokenBifrostBtcusd,
    tokenBifrostEverdexStakeLp,
  } = useEnv();

  // wallet
  const { account } = useWallet();

  // everdex
  const {
    isLpRewardOpen,
    lpRewardApr,
    poolApr,
    apr,
    isLoading: isLoadingEverdex,
  } = useEverdex();

  // balance
  const { balance: balanceBtcusd } = useBalance(tokenBifrostBtcusd);
  const { balance: balanceEverdexStakeLp } = useBalance(
    tokenBifrostEverdexStakeLp
  );

  // store
  const [, setDialogAction] = useAtom(dialogActionAtom);
  const [, setIsShowConnectWallet] = useAtom(isShowConnectWalletAtom);

  // state
  const [amount, setAmount] = useState('');

  // memo
  const errorMessage = useMemo(() => {
    if (balanceBtcusd.lte(0)) {
      return t('earn:lackBtcUSD');
    }

    if (balanceBtcusd.lt(amount)) {
      return t('earn:lackBalance');
    }

    return '';
  }, [balanceBtcusd, amount, t]);
  const isConnected = useMemo(() => isValidAddress(account), [account]);
  const isAddLiquidityDisable = useMemo(
    () => !!errorMessage || !amount,
    [errorMessage, amount]
  );
  const isStakeDisable = useMemo(
    () => !isNormalPositive(balanceEverdexStakeLp, true),
    [balanceEverdexStakeLp]
  );
  const isErrorMessageShow = useMemo(
    () => account && errorMessage && amount,
    [account, errorMessage, amount]
  );
  const isLoading = useMemo(
    () => !!(account && !syncedEverdex && isLoadingEverdex),
    [account, syncedEverdex, isLoadingEverdex]
  );

  // handler
  const handleInputAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAmount(parseNumberInput(e.target.value));
    },
    []
  );
  const handleMaxClick = useCallback(() => {
    setAmount(formatDecimal(balanceBtcusd, tokenBifrostBtcusd?.decimals ?? 18));
  }, [balanceBtcusd, tokenBifrostBtcusd?.decimals]);
  const handleAddLiquidityClick = useCallback(async () => {
    if (isConnected) {
      eventEverdexAddLiquidityClick(isTestnet);
      setDialogAction({
        isShow: true,
        amount: formatDecimal(amount, tokenBifrostBtcusd?.decimals ?? 18),
        type: 'everdex:addLiquidity',
      });
      setAmount('');
    } else {
      setIsShowConnectWallet(true);
    }
  }, [
    amount,
    isConnected,
    setIsShowConnectWallet,
    setDialogAction,
    tokenBifrostBtcusd?.decimals,
    isTestnet,
  ]);
  const handleStakeClick = useCallback(async () => {
    if (isConnected) {
      setDialogAction({
        isShow: true,
        type: 'everdex:lpStake',
      });
      setAmount('');
    } else {
      setIsShowConnectWallet(true);
    }
  }, [isConnected, setIsShowConnectWallet, setDialogAction]);

  // effect
  useEffect(() => {
    if (!isConnected) {
      setAmount('');
    }
  }, [isConnected]);

  return (
    <StyledGlassPanel
      as={motion.div}
      variants={dashboardCardVariant}
      $flex
      $direction="column"
      $gap={40}
      $padding="xl"
      $rounded="xl"
    >
      <FlexBox $flex $direction="column" $gap="xl">
        <FlexBox $flex $gap="xxs" $align="center" $height={36}>
          <IconEverdex />
          <StyledText as="h2" $fontWeight="bold" $fontSize="xl">
            {t('earn:depositToEverdex')}
          </StyledText>
          <CustomTooltip>
            <CustomTooltipContent style={{ whiteSpace: 'pre', left: '110px' }}>
              <Apy>
                APY{' '}
                {isLoading ? (
                  <Skeleton $width={100} />
                ) : (
                  <>
                    <strong>{formatRate(apr)}%</strong>
                  </>
                )}
              </Apy>
            </CustomTooltipContent>
            <IconTooltipArrow />
          </CustomTooltip>
        </FlexBox>
        <Description as="p">
          {t('earn:depositToEverdexDescription', {
            symbolPair: everdexLpRewardPoolPairSymbol,
          })}
        </Description>
      </FlexBox>
      <FlexBox $flex $gap="md">
        <FlexBox $flex $direction="column" $gap="xs">
          <StyledText $textColor="textGray" $fontWeight="medium">
            {t('earn:depositToken')}
          </StyledText>
          <TokenWrapper $flex $gap={8}>
            <FlexBox $width={148}>
              <LogoBtcusd />
            </FlexBox>
          </TokenWrapper>
        </FlexBox>
        <InputContainer $flex $direction="column" $gap="lg">
          <FlexBox $flex $direction="column" $gap="md">
            <FlexBox $flex $direction="column" $gap={40}>
              <InputWrapper $flex $direction="column" $gap="xs">
                <StyledText $textColor="textGray" $fontWeight="medium">
                  {t('earn:balance')}
                </StyledText>
                {isLoading ? (
                  <Skeleton $height={64} />
                ) : (
                  <>
                    <Input
                      as="div"
                      $padding="md"
                      $textSize="lg"
                      $fontWeight="bold"
                      $rounded="lg"
                    >
                      {formatAmountDetail(balanceBtcusd)}
                    </Input>
                  </>
                )}
              </InputWrapper>
              <InputWrapper $flex $direction="column" $gap="xs">
                <StyledText
                  as="label"
                  htmlFor="amount"
                  $textColor="textGray"
                  $fontWeight="medium"
                >
                  {t('earn:depositAmount')}
                </StyledText>
                <FlexBox style={{ position: 'relative' }}>
                  <Input
                    id="amount"
                    $padding="md"
                    $textSize="lg"
                    $fontWeight="bold"
                    $rounded="lg"
                    style={{ width: '100%' }}
                    disabled={!isConnected}
                    value={amount}
                    onChange={handleInputAmountChange}
                    placeholder={t('earn:placeholder')}
                  />
                  <MaxButton
                    $padding={{
                      x: 'xs',
                      y: 'xxs',
                    }}
                    $variant="primary"
                    onClick={handleMaxClick}
                    disabled={!account}
                  >
                    Max
                  </MaxButton>
                </FlexBox>
              </InputWrapper>
            </FlexBox>
            {isErrorMessageShow && (
              <StyledWarnMessage padding="xxs" rounded="xs" textSize="xs">
                <span>{errorMessage}</span>
              </StyledWarnMessage>
            )}
          </FlexBox>
          <FlexBox $flex $justify="flex-end">
            <FlexBox $flex $gap="lg">
              <Tooltip placement="top" offset={16}>
                <TooltipTrigger asChild>
                  <EverdexButton
                    disabled={isConnected && isAddLiquidityDisable}
                    padding="md"
                    gap="xs"
                    variant="primary"
                    fontWeight="bold"
                    isLpRewardOpen={isLpRewardOpen}
                    onClick={handleAddLiquidityClick}
                  >
                    <FlexBox
                      $flex
                      $direction="column"
                      $gap="xxs"
                      $align="flex-start"
                    >
                      <StyledText
                        $fontSize={isLpRewardOpen ? 'lg' : 'md'}
                        $fontWeight="bold"
                        $whiteSpace="nowrap"
                      >
                        {t('earn:everdexSupply')}
                      </StyledText>
                      {isLpRewardOpen && (
                        <BadgeContainer>
                          <StyledText
                            $fontSize="xs"
                            $fontWeight="bold"
                            $whiteSpace="nowrap"
                          >
                            APY
                          </StyledText>
                          {isLoading ? (
                            <Skeleton $width={70} $height={24} />
                          ) : (
                            <>
                              <StyledText
                                $fontSize="md"
                                $textColor="green"
                                $fontWeight="black"
                                $whiteSpace="nowrap"
                              >
                                {formatRate(poolApr)}%
                              </StyledText>
                            </>
                          )}
                        </BadgeContainer>
                      )}
                    </FlexBox>
                  </EverdexButton>
                </TooltipTrigger>
                {!isConnected ? (
                  <TooltipContent>
                    <TooltipArrow />
                    <StyledText $fontSize="md" $fontWeight="bold">
                      {t('dashboard:connectWalletFirst')}
                    </StyledText>
                  </TooltipContent>
                ) : (
                  ''
                )}
              </Tooltip>
              {isLpRewardOpen && (
                <Tooltip placement="top" offset={16}>
                  <TooltipTrigger asChild>
                    <EverdexButton
                      disabled={isConnected && isStakeDisable}
                      padding="md"
                      gap="xs"
                      variant="primary"
                      fontWeight="bold"
                      onClick={handleStakeClick}
                    >
                      <ShimmerContainer />
                      <FlexBox
                        $flex
                        $direction="column"
                        $gap="xxs"
                        $align="flex-start"
                      >
                        <StyledText
                          $fontSize="lg"
                          $fontWeight="bold"
                          $whiteSpace="nowrap"
                        >
                          {t('earn:everdexStaking')}
                        </StyledText>
                        <BadgeContainer>
                          <StyledText
                            $fontSize="xs"
                            $fontWeight="bold"
                            $whiteSpace="nowrap"
                          >
                            APY
                          </StyledText>
                          {isLoading ? (
                            <Skeleton $width={70} $height={24} />
                          ) : (
                            <>
                              <StyledText
                                $fontSize="md"
                                $textColor="green"
                                $fontWeight="black"
                                $whiteSpace="nowrap"
                              >
                                {formatRate(lpRewardApr)}%
                              </StyledText>
                            </>
                          )}
                        </BadgeContainer>
                      </FlexBox>
                    </EverdexButton>
                  </TooltipTrigger>
                  {!isConnected ? (
                    <TooltipContent>
                      <TooltipArrow />
                      <StyledText $fontSize="md" $fontWeight="bold">
                        {t('dashboard:connectWalletFirst')}
                      </StyledText>
                    </TooltipContent>
                  ) : isStakeDisable ? (
                    <TooltipContent>
                      <TooltipArrow />
                      <StyledText $fontSize="md" $fontWeight="bold">
                        {t('earn:everdexLpTooltip')}
                      </StyledText>
                    </TooltipContent>
                  ) : (
                    ''
                  )}
                </Tooltip>
              )}
            </FlexBox>
          </FlexBox>
        </InputContainer>
      </FlexBox>
    </StyledGlassPanel>
  );
}
